/**
 * India-first phone normalisation. Stored and compared as E.164 (`+91…`).
 *
 * Accepts `9876543210`, `09876543210`, `919876543210`, `+919876543210`.
 */
export function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  let national = digits;
  if (national.startsWith("91") && national.length === 12) {
    national = national.slice(2);
  } else if (national.startsWith("0") && national.length === 11) {
    national = national.slice(1);
  }

  // Reserved App Store review demo number (not a real Indian mobile prefix).
  if (national === "1111111111") {
    return "+911111111111";
  }

  if (national.length !== 10 || !/^[6-9]\d{9}$/.test(national)) {
    return null;
  }

  return `+91${national}`;
}

/** `+919876543210` → `+91 98765 43210` for display. */
export function formatPhoneDisplay(phone: string): string {
  const normalized = normalizePhone(phone) ?? phone;
  const match = /^\+91(\d{5})(\d{5})$/.exec(normalized);
  if (!match) return normalized;
  return `+91 ${match[1]} ${match[2]}`;
}

export function isValidPhone(raw: string): boolean {
  return normalizePhone(raw) !== null;
}

/**
 * Razorpay Checkout prefill for India works most reliably as a 10-digit
 * national number (the UI already shows +91). Falls back to E.164.
 */
export function razorpayContactPrefill(raw: string): string | null {
  const normalized = normalizePhone(raw);
  if (!normalized) return null;
  const national = normalized.replace(/^\+91/, "");
  return national.length === 10 ? national : normalized;
}
